import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Switch,
  message,
  Modal,
  Progress,
  notification,
  Checkbox,
  TimePicker,
} from "antd";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  CloseOutlined,
  LoadingOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { RxUpload } from "react-icons/rx";
import { HiOutlineVideoCamera } from "react-icons/hi2";
import { FaBox, FaPlay, FaStop } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import {
  useCreateQuestionMutation,
  useDeleteQuestionMutation,
  useGetFormQuestionsQuery,
  useGetInterpretersQuery,
  useGetOrganizationsQuery,
  useGetQuestionByIdQuery,
  useGetQuestionsQuery,
  useGetVideoCatalogQuery,
  useUpdateQuestionMutation,
  useGeneratePresignedUrlMutation,
  useUploadVideoMutation,
  useCreateQuestionTemplateMutation,
  useUploadFileMutation,
} from "../../../store/api/apiSlice";
import VideoRecorder from "../../../components/VideoRecorder/VideoRecorder";
import VideoPreview from "../../../components/VideoPreview/VideoPreview";
import PageHeader from "./PageHeader";
import QuestionPageHeader from "./PageHeader";
import moment from "moment";
import VideoBucketModal from "../../../components/Modals/VideoBucketModal";
import { getScalePlaceholder, languageOptions } from "../../../utils";
import UploadConfirmationModal from "../../../components/Modals/UploadConfirmationModal";
import { Frown, Meh, Smile, SmileIcon, SmilePlus, Star } from "lucide-react";
import {
  generateFileName,
  uploadToPresignedUrl,
} from "../../../utils/presignedUploader";
import {
  getSliderScaleSelectOptions,
  inputTypeOptions,
  parseSliderOptionLabels,
  joinSliderOptionLabels,
  resolveSliderScaleType,
  getSliderOptionsValidationError,
  isMissingSliderOptionLabel,
  buildChoiceInputResponseFields,
  resolveShowLogosFlag,
  resolveSliderToggleFlags,
  toSliderToggleValue,
  getChoiceLogosValidationError,
} from "./constants";
import ChoiceOptionsEditor from "./ChoiceOptionsEditor";
import RadioListEditor from "./RadioListEditor";
import {
  getRadioListSetupError,
  getRadioListEditorState,
  buildRadioListResponseFields,
  getRadioListTypeLabel,
  isRadioListInputType,
} from "../../../utils/radioList";

const MAX_TEMPLATE_VIDEO_UPLOAD_BYTES = 150 * 1024 * 1024;
const MAX_TEMPLATE_VIDEO_UPLOAD_MB = 150;

const { Option } = Select;

const dateTypes = [
  { value: "date-range", label: "Date Range" },
  { value: "single-date", label: "Single Date" },
  { value: "date-time", label: "Date & Time" },
];

const fileTypes = [
  { value: "image/*", label: "JPG, PNG, JPEG" },
  { value: "application/pdf", label: "PDF" },
  { value: "video/*", label: "Video Files" },
];

const getQueryParams = (filters) => {
  const params = {};

  if (filters?.search?.trim()) {
    params.search = filters.search.trim();
  }

  if (filters?.organization) {
    params.organization = filters.organization;
  }

  if (filters?.interpreter) {
    params.interpreter = filters.interpreter;
  }

  if (filters?.language) {
    params.language = filters.language;
  }

  return params; // Return the params object instead of false
};
const CreateQuestionTemplate = ({ mode = "add" }) => {
  const [mainForm] = Form.useForm();
  // const {formId} = useParams();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteQuestion] = useDeleteQuestionMutation();
  const [updateQuestion] = useUpdateQuestionMutation();
  const { formId: paramFormId, questionId } = useParams();
  const formId = paramFormId || "67aed95ea687f32472216549";
  const [conditionalEnabled, setConditionalEnabled] = useState(false);
  // const { data: videosData, isLoading: videosLoading } = useGetVideoCatalogQuery(getQueryParams());
  const [showUploadConfirmation, setShowUploadConfirmation] = useState(false);
  const [pendingCatalogVideo, setPendingCatalogVideo] = useState(null);

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoFilters, setVideoFilters] = useState({});
  const { data: videosData, isLoading: videosLoading } =
    useGetVideoCatalogQuery(getQueryParams(videoFilters));
  const { data: orgsData } = useGetOrganizationsQuery();
  const { data: interpretersData } = useGetInterpretersQuery();
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadModalForm] = Form.useForm(); // Create a new form instance for the upload modal
  const [uploading, setUploading] = useState(false);

  const [uploadVideo] = useUploadVideoMutation();

  const buttonOptions = [
    { value: "start", label: "Start" },
    { value: "next", label: "Next" },
    { value: "finish", label: "Finish" },
  ];

  // const [deleteQuestion] = useDeleteQuestionMutation();

  const { data: questionData, isLoading } =
    mode === "edit"
      ? useGetQuestionByIdQuery(questionId, {
          refetchOnMountOrArgChange: true,
        })
      : { data: null, isLoading: false };
  const [videoPreview, setVideoPreview] = useState(null);

  const handleVideoDelete = () => {
    setUploadedVideo(null);
    setVideoPreview(null);
    setRecordedVideo(null);
    setSelectedVideo(null);
  };
  // States
  const [inputs, setInputs] = useState([
    {
      id: `${Date.now()}`,
      type: "text",
      title: "",
      require: false,
      options: undefined,
      dateType: undefined,
      fileType: undefined,
      recordingLimit: undefined,
    },
  ]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedBucketVideo, setSelectedBucketVideo] = useState(null);

  // Refs
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // API Mutations
  const [createQuestion] = useCreateQuestionMutation();
  const [createQuestionTemplate] = useCreateQuestionTemplateMutation();
  const [generatePresignedUrl] = useGeneratePresignedUrlMutation();
  const [uploadFile] = useUploadFileMutation();
  const { data: questionsData } = useGetFormQuestionsQuery(formId);
  const existingNumbers = questionsData?.data?.map((q) => q.number);
  const nextNumber =
    existingNumbers?.length > 0 ? Math.max(...existingNumbers) + 1 : 0;
  const [bucketModalVisible, setBucketModalVisible] = useState(false);
  const getInputTypeLabel = (type) =>
    inputTypeOptions.find((option) => option.value === type)?.label || type;

  // Add this handler
  const handleBucketVideoSelect = (videoUrl) => {
    if (videoUrl) {
      setUploadedVideo(videoUrl);
      setVideoPreview(videoUrl);
    }
    setBucketModalVisible(false);
  };

  useEffect(() => {
    mainForm.setFields([
      {
        name: "questionNumber",
        value: nextNumber,
        disabled: true,
      },
    ]);

    if (mode === "edit" && questionData?.data) {
      const data = questionData.data;

      // Set base form fields
      const formValues = {
        questionNumber: data.number,
        questionTitle: data.title,
        description: data.description,
        showTerms: data.showTerms ?? false,
        showAnonymous: data.showAnonymous ?? false,
      };

      // Handle conditional trigger data
      if (data.condition && data.condition.length > 0) {
        const condition = data.condition[0]; // Get the first condition
        setConditionalEnabled(true);
        formValues.conditional_question = condition.question;
        formValues.conditional_response = condition.response;
      } else {
        setConditionalEnabled(false);
      }

      // Set video preview if exists
      if (data.video) {
        setUploadedVideo(data.video);
        setVideoPreview(data.video);
      }

      if (data.response && Array.isArray(data.response)) {
        const formattedInputs = data.response.map((input, index) => {
          const inputId = `${Date.now()}-${index}`;
          formValues[`input_title_${inputId}`] = input.input_title;
          formValues[`required_${inputId}`] = input.require;

          if (input.input_type === "slider") {
            const scaleType = resolveSliderScaleType(
              input.scale_type,
              input.input_options,
            );
            const optionLabels = parseSliderOptionLabels(
              input.input_options,
              scaleType,
            );
            const joinedOptions = joinSliderOptionLabels(optionLabels);

            const sliderFlags = resolveSliderToggleFlags(input);
            formValues[`slider_options_${inputId}`] = joinedOptions;
            formValues[`scale_type_${inputId}`] = scaleType;
            formValues[`show_emojis_${inputId}`] = sliderFlags.show_emojis;
            formValues[`show_stars_${inputId}`] = sliderFlags.show_stars;
            formValues[`show_only_emojis_${inputId}`] =
              sliderFlags.show_only_emojis;
            optionLabels.forEach((label, pointIndex) => {
              formValues[`slider_option_${inputId}_${pointIndex}`] = label;
            });
          }

          if (
            input.input_type === "checkbox" ||
            input.input_type === "radio" ||
            input.input_type === "dropdown"
          ) {
            formValues[`show_logos_${inputId}`] = resolveShowLogosFlag(input);
          }

          return {
            id: inputId,
            type: input.input_type,
            title: input.input_title,
            require: input.require,
            options: input.input_options
              ? input.input_type === "slider"
                ? joinSliderOptionLabels(
                    parseSliderOptionLabels(
                      input.input_options,
                      resolveSliderScaleType(
                        input.scale_type,
                        input.input_options,
                      ),
                    ),
                  )
                : ["dropdown", "checkbox", "radio"].includes(input.input_type)
                  ? Array.isArray(input.input_options)
                    ? input.input_options.join("\n")
                    : input.input_options
                  : Array.isArray(input.input_options)
                    ? input.input_options.join("\n")
                    : input.input_options
              : "",
            ...(isRadioListInputType(input.input_type)
              ? getRadioListEditorState(input)
              : {}),
            ...resolveSliderToggleFlags(input),
            scale_type: resolveSliderScaleType(
              input.scale_type,
              input.input_options,
            ),
            dateType: input.date_type || "date-range",
            fileType: input.file_type || "image/*",
            recordingLimit: input.recording_limit || "00:00:00",
            show_logos: resolveShowLogosFlag(input),
            option_logos: Array.isArray(input.option_logos)
              ? input.option_logos
              : [],
          };
        });

        setInputs(formattedInputs);
      }

      mainForm.setFieldsValue(formValues);
    }
  }, [questionData, mode, mainForm, nextNumber]);
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      streamRef.current.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };
  const handleUpload = async (values) => {
    try {
      setUploading(true);

      const videoData = {
        title: values.title,
        description: values.description,
        organization: values.organization,
        interpreter: values.interpreter,
        language: values.language,
        url: pendingCatalogVideo,
        type: "video/mp4",
      };

      await uploadVideo(videoData).unwrap();

      notification.success({
        message: "Success",
        description: "Video uploaded to catalog successfully",
      });
      setUploadModalVisible(false);
      uploadModalForm.resetFields();
      setShowUploadConfirmation(false);
      setPendingCatalogVideo(null);
    } catch (error) {
      notification.error({
        message: "Error",
        description: error.message || "Failed to upload video to catalog",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadToCatalog = () => {
    setShowUploadConfirmation(false);
    setUploadModalVisible(true);
  };
  const handleCloseUploadConfirmation = () => {
    setShowUploadConfirmation(false);
    setPendingCatalogVideo(null);
  };
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_TEMPLATE_VIDEO_UPLOAD_BYTES) {
      notification.error({
        message: "File too large",
        description: `Please upload a video file smaller than ${MAX_TEMPLATE_VIDEO_UPLOAD_MB} MB`,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const contentType =
        file.type ||
        (/\.(mov|qt)$/i.test(file.name || "") ? "video/quicktime" : "video/mp4");
      const fileName = generateFileName(contentType, "question-video");

      const presignedResponse = await generatePresignedUrl({
        fileName,
        contentType,
        expiresIn: 600,
      }).unwrap();

      if (!presignedResponse?.url) {
        throw new Error("Failed to get presigned URL");
      }

      await uploadToPresignedUrl(
        presignedResponse.url,
        file,
        contentType,
        (progress) => setUploadProgress(Math.round(progress)),
      );

      const fileUrl = presignedResponse.fileUrl;
      if (!fileUrl) {
        throw new Error("No file URL returned from server");
      }

      setUploadedVideo(fileUrl);
      setVideoPreview(fileUrl);
      setPendingCatalogVideo(fileUrl);
      setShowUploadConfirmation(true);
      notification.success({
        message: "Upload Successful",
        description: "Video has been uploaded successfully",
      });
    } catch (error) {
      console.error("Upload error:", error);
      notification.error({
        message: "Upload Failed",
        description:
          error?.data?.message ||
          error?.message ||
          "Failed to upload video. Please try again.",
      });
      e.target.value = "";
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const questionNumber = questionData?.data?.number ?? nextNumber;

  // Input Management Functions
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(inputs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setInputs(items);
  };

  const addInput = (type) => {
    const newId = `${Date.now()}`;
    const newInput = {
      id: newId,
      type,
      title: type === "signature" ? "Signature" : "",
      require: false,
      options:
        type === "slider" ||
        type === "dropdown" ||
        type === "checkbox" ||
        type === "radio" ||
        isRadioListInputType(type)
          ? ""
          : undefined,
      rowOptions: isRadioListInputType(type) ? "" : undefined,
      scale_type: type === "slider" ? null : undefined,
      dateType: type === "date" ? "date-range" : undefined,
      fileType:
        type === "file" || type === "multiple-files" ? "image/*" : undefined,
      recordingLimit: type === "record-video" ? "00:00:30" : undefined,
      show_logos:
        type === "checkbox" || type === "radio" || type === "dropdown"
          ? false
          : undefined,
      option_logos:
        type === "checkbox" || type === "radio" || type === "dropdown"
          ? []
          : undefined,
    };

    setInputs((prevInputs) => [...prevInputs, newInput]);
    mainForm.setFieldsValue({
      [`input_title_${newId}`]: type === "signature" ? "Signature" : "",
      [`required_${newId}`]: false,
    });

    if (type === "slider") {
      mainForm.setFieldsValue({
        [`slider_options_${newId}`]: "",
        [`scale_type_${newId}`]: null,
        [`show_emojis_${newId}`]: false,
        [`show_stars_${newId}`]: false,
        [`show_only_emojis_${newId}`]: false,
      });
    }

    if (type === "checkbox" || type === "radio" || type === "dropdown") {
      mainForm.setFieldsValue({
        [`show_logos_${newId}`]: false,
      });
    }

    setIsAddingNew(false);
  };

  const removeInput = (id) => {
    setInputs(inputs.filter((input) => input.id !== id));
  };

  const updateInput = (id, fieldOrPatch, value) => {
    setInputs((prev) =>
      prev.map((input) => {
        if (input.id !== id) return input;

        if (
          fieldOrPatch != null &&
          typeof fieldOrPatch === "object" &&
          !Array.isArray(fieldOrPatch)
        ) {
          return { ...input, ...fieldOrPatch };
        }

        return { ...input, [fieldOrPatch]: value };
      }),
    );
  };

  const getReactEmoji = (index, total) => {
    // Get the correct React component based on index and scale
    const total_str = total.toString();

    if (total_str === "4") {
      switch (index) {
        case 0:
          return <Frown size={18} color="#FF4D4F" />;
        case 1:
          return <Meh size={18} color="#FFA940" />;
        case 2:
          return <Smile size={18} color="#52C41A" />;
        case 3:
          return <SmilePlus size={18} color="#1890FF" />;
        default:
          return <SmileIcon size={18} color="#FADB14" />;
      }
    } else if (total_str === "5") {
      switch (index) {
        case 0:
          return <Frown size={18} color="#FF4D4F" />;
        case 1:
          return <Meh size={18} color="#FFA940" />;
        case 2:
          return <SmileIcon size={18} color="#FADB14" />;
        case 3:
          return <Smile size={18} color="#52C41A" />;
        case 4:
          return <SmilePlus size={18} color="#1890FF" />;
        default:
          return <SmileIcon size={18} color="#FADB14" />;
      }
    } else if (total_str === "7") {
      switch (index) {
        case 0:
          return <Frown size={18} color="#FF4D4F" />;
        case 1:
          return <Frown size={18} color="#FF7A45" />;
        case 2:
          return <Meh size={18} color="#FFA940" />;
        case 3:
          return <SmileIcon size={18} color="#FADB14" />;
        case 4:
          return <Smile size={18} color="#73D13D" />;
        case 5:
          return <Smile size={18} color="#52C41A" />;
        case 6:
          return <SmilePlus size={18} color="#1890FF" />;
        default:
          return <SmileIcon size={18} color="#FADB14" />;
      }
    }

    // Default case
    return <SmileIcon size={18} color="#FADB14" />;
  };

  const getReactStar = (index, total) => {
    // All stars in yellow color, like standard rating systems
    const yellowColor = "#FFD700"; // Gold yellow
    
    return (
      <Star
        size={28}
        color={yellowColor}
        fill={yellowColor}
      />
    );
  };

  const handleSubmit = async () => {
    try {
      await mainForm.validateFields();

      for (const input of inputs) {
        if (input.type !== "slider") continue;

        const showOnlyEmojis = toSliderToggleValue(
          mainForm.getFieldValue(`show_only_emojis_${input.id}`) ??
            input.show_only_emojis,
        );
        const scaleType = mainForm.getFieldValue(`scale_type_${input.id}`);
        const validationError = getSliderOptionsValidationError({
          options: input.options,
          scalePoints: scaleType,
          showOnlyEmojis,
          inputTitle: input.title || "Slider",
        });

        if (validationError) {
          notification.error({
            message: "Incomplete slider setup",
            description: validationError,
          });
          return;
        }
      }

      for (const input of inputs) {
        if (
          input.type !== "radio" &&
          input.type !== "checkbox" &&
          input.type !== "dropdown"
        ) {
          continue;
        }

        const showLogos = Boolean(
          mainForm.getFieldValue(`show_logos_${input.id}`) ??
            resolveShowLogosFlag(input),
        );
        const logosError = getChoiceLogosValidationError({
          inputType: input.type,
          options: input.options,
          showLogos,
          optionLogos: input.option_logos,
          inputTitle:
            input.title ||
            (input.type === "radio"
              ? "Radio"
              : input.type === "dropdown"
                ? "Dropdown"
                : "Checkbox"),
        });

        if (logosError) {
          notification.error({
            message: "Incomplete logos setup",
            description: logosError,
          });
          return;
        }
      }

      for (const input of inputs) {
        if (!isRadioListInputType(input.type)) continue;

        const setupError = getRadioListSetupError({
          headers: input.options,
          rows: input.rowOptions,
          inputTitle: input.title || getRadioListTypeLabel(input.type),
        });

        if (setupError) {
          notification.error({
            message: "Incomplete radio list setup",
            description: setupError,
          });
          return;
        }
      }

      const values = mainForm.getFieldsValue();
      const response = inputs.map((input) => {
        const baseResponse = {
          input_type: input.type,
          input_title: input.title,
          require: input?.require || false,
        };

        switch (input.type) {
          case "slider":
            const show_only_emojis = toSliderToggleValue(
              mainForm.getFieldValue(`show_only_emojis_${input.id}`) ??
                input.show_only_emojis,
            );
            const show_emojis = toSliderToggleValue(
              mainForm.getFieldValue(`show_emojis_${input.id}`) ??
                input.show_emojis,
            );
            const show_stars = toSliderToggleValue(
              mainForm.getFieldValue(`show_stars_${input.id}`) ??
                input.show_stars,
            );
            const scale_type = mainForm.getFieldValue(
              `scale_type_${input.id}`,
            );
            const totalOptions = parseInt(scale_type, 10);

            let input_options = [];

            if (show_only_emojis) {
              input_options = Array.from(
                { length: totalOptions },
                (_, i) => `${i}`,
              );
            } else {
              input_options = parseSliderOptionLabels(
                input.options,
                totalOptions,
              );
            }

            return {
              ...baseResponse,
              input_options: input_options,
              scale_type: scale_type,
              show_emojis: show_emojis,
              show_stars: show_stars,
              show_only_emojis: show_only_emojis,
              slider_config: {
                scale_points: totalOptions,
                show_only_emojis: show_only_emojis,
                show_emojis: show_emojis,
                show_stars: show_stars,
                options: input_options,
              },
            };
          case "dropdown":
          case "checkbox":
          case "radio":
            return {
              ...baseResponse,
              ...buildChoiceInputResponseFields(input, mainForm),
            };
          case "checkbox-list":
          case "radio-list":
            return {
              ...baseResponse,
              ...buildRadioListResponseFields(input),
            };
          case "date":
            return {
              ...baseResponse,
              date_type: input.dateType,
            };
          case "file":
          case "multiple-files":
            return {
              ...baseResponse,
              file_type: input.fileType,
            };
          case "record-video":
            return {
              ...baseResponse,
              recording_limit: input.recordingLimit,
            };
          default:
            return baseResponse;
        }
      });

      const questionData = {
        video: uploadedVideo,
        number: Number(values.questionNumber),
        title: values.questionTitle,
        description: values.description,
        response,
        showTerms: values.showTerms,
        showAnonymous: values.showAnonymous,
        showButton: values.showButton,
        buttonType: values.buttonType,
        condition: conditionalEnabled
          ? [
              {
                trigger: true,
                question: values.conditional_question,
                response: values.conditional_response,
                redirect_to: String(values.questionNumber),
              },
            ]
          : null,
        is_template: true,
      };

      if (mode === "edit") {
        await updateQuestion({
          formId,
          questionId,
          data: questionData,
        }).unwrap();
      } else {
        await createQuestionTemplate(questionData).unwrap();
      }

      navigate(-1);
    } catch (error) {
    }
  };

  useEffect(() => {
    if (mode === "edit" && questionData?.data) {
      const data = questionData.data;
      const formValues = {
        questionNumber: data.number,
        questionTitle: data.title,
        description: data.description,
        showButton: data.showButton ?? true,
        buttonType: data.buttonType || (data.number === 0 ? "start" : "next"),
      };

      if (data.response && Array.isArray(data.response)) {
        const formattedInputs = data.response.map((input, index) => {
          const inputId = `${Date.now()}-${index}`;
          formValues[`input_title_${inputId}`] = input.input_title;
          formValues[`required_${inputId}`] = input.require;

          if (input.input_type === "slider") {
            const scaleType = resolveSliderScaleType(
              input.scale_type,
              input.input_options,
            );
            const optionLabels = parseSliderOptionLabels(
              input.input_options,
              scaleType,
            );
            const joinedOptions = joinSliderOptionLabels(optionLabels);

            const sliderFlags = resolveSliderToggleFlags(input);
            formValues[`slider_options_${inputId}`] = joinedOptions;
            formValues[`scale_type_${inputId}`] = scaleType;
            formValues[`show_emojis_${inputId}`] = sliderFlags.show_emojis;
            formValues[`show_stars_${inputId}`] = sliderFlags.show_stars;
            formValues[`show_only_emojis_${inputId}`] =
              sliderFlags.show_only_emojis;
            optionLabels.forEach((label, pointIndex) => {
              formValues[`slider_option_${inputId}_${pointIndex}`] = label;
            });
          }

          if (
            input.input_type === "checkbox" ||
            input.input_type === "radio" ||
            input.input_type === "dropdown"
          ) {
            formValues[`show_logos_${inputId}`] = resolveShowLogosFlag(input);
          }

          return {
            id: inputId,
            type: input.input_type,
            title: input.input_title,
            require: input.require,
            options: input.input_options
              ? input.input_type === "slider"
                ? joinSliderOptionLabels(
                    parseSliderOptionLabels(
                      input.input_options,
                      resolveSliderScaleType(
                        input.scale_type,
                        input.input_options,
                      ),
                    ),
                  )
                : ["dropdown", "checkbox", "radio"].includes(input.input_type)
                  ? Array.isArray(input.input_options)
                    ? input.input_options.join("\n")
                    : input.input_options
                  : Array.isArray(input.input_options)
                    ? input.input_options.join("\n")
                    : input.input_options
              : "",
            ...(isRadioListInputType(input.input_type)
              ? getRadioListEditorState(input)
              : {}),
            ...resolveSliderToggleFlags(input),
            scale_type: resolveSliderScaleType(
              input.scale_type,
              input.input_options,
            ),
            dateType: input.date_type || "date-range",
            fileType: input.file_type || "image/*",
            recordingLimit: input.recording_limit || "00:00:00",
            show_logos: resolveShowLogosFlag(input),
            option_logos: Array.isArray(input.option_logos)
              ? input.option_logos
              : [],
          };
        });

        setInputs(formattedInputs);
      }

      mainForm.setFieldsValue(formValues);
    } else {
      mainForm.setFieldsValue({
        showButton: true,
        buttonType: questionNumber === 0 ? "start" : "next",
      });
    }
  }, [questionData, mode, mainForm, questionNumber]);
  const handleDelete = () => {
    Modal.confirm({
      title: "Delete Question",
      content: "Are you sure you want to delete this question?",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteQuestion({ formId, questionId }).unwrap();
          navigate(-1);
        } catch (error) {
          notification.error({ message: "Failed to delete question" });
        }
      },
    });
  };

  const renderVideoModal = () => (
    <Modal
      open={videoModalVisible}
      onCancel={() => {
        // Cleanup when modal closes
        if (videoRef.current) {
          const tracks = videoRef.current.srcObject?.getTracks();
          tracks?.forEach((track) => track.stop());
          videoRef.current.srcObject = null;
        }
        if (isRecording) {
          stopRecording();
        }
        setVideoModalVisible(false);
      }}
      maskClosable={false}
      destroyOnClose={true}
      afterClose={() => {
        // Additional cleanup after modal closes
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      }}
      footer={null}
      title={
        <div className="flex items-center gap-2">
          <HiOutlineVideoCamera className="text-xl" />
          <span>Record Video</span>
        </div>
      }
      width={600}
      className="video-recording-modal"
    >
      <VideoRecorder
        onVideoRecorded={(videoUrl) => {
          setVideoPreview(videoUrl);
          setUploadedVideo(videoUrl);
          setVideoModalVisible(false);
        }}
        maxDuration={180}
      />
    </Modal>
  );
  const renderUploadProgress = () =>
    isUploading && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-xl w-96">
          <div className="text-center mb-4">
            <LoadingOutlined style={{ fontSize: 24 }} />
            <div className="mt-2">Uploading Video...</div>
          </div>
          <Progress percent={uploadProgress} status="active" />
        </div>
      </div>
    );

  const QuestionButtonConfig = ({ questionNumber }) => {
    const [isRequired, setIsRequired] = useState(true);

    useEffect(() => {
      // Initialize button values in mainForm if they don't exist
      const currentValues = mainForm.getFieldsValue();

      if (!currentValues.showButton && !currentValues.buttonType) {
        mainForm.setFieldsValue({
          showButton: true,
          buttonType: questionNumber === 0 ? "start" : "next",
        });
      }
    }, [questionNumber]);

    return (
      <div className="border p-4 rounded-xl">
        <h3 className="text-lg font-medium mb-4">Button Configuration</h3>
        <div className="space-y-4">
          <Form.Item
            name="showButton"
            valuePropName="checked"
            initialValue={true}
          >
            <Checkbox onChange={(e) => setIsRequired(e.target.checked)}>
              Show Button
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="buttonType"
            label="Button Text"
            rules={[
              { required: isRequired, message: "Please select button text" },
            ]}
          >
            <Select
              disabled={!isRequired}
              placeholder="Select button text"
              className="w-full h-11"
            >
              {buttonOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </div>
    );
  };

  const getEmoji = (index, total) => {
    const emojiSets = {
      4: ["😠", "🙁", "😊", "😃"],
      5: ["😠", "🙁", "😐", "😊", "😃"],
      7: ["😠", "😣", "🙁", "😐", "🙂", "😊", "😃"],
    };
    return emojiSets[total.toString()]?.[index] || "😐";
  };
  const preventDragHandling = (e) => {
    // Stop propagation of mouse events in Select components to prevent triggering drag
    e.stopPropagation();
  };
  return (
    <Form
      form={mainForm}
      layout="vertical"
      onFinish={handleSubmit}
      className="space-y-6 bg-white my-6"
      // footer={null}
    >
      {" "}
      {/* <Form></Form> */}
      <QuestionPageHeader
        mode={mode}
        questionNumber={" " + questionNumber}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-2 gap-6">
        {/* Left Side - Video Upload/Record */}
        <div className=" border border-secondary rounded-xl p-8 min-h-[27px] flex  flex-wrap  items-center justify-center gap-4">
          {videoPreview ? (
            <div className="">
              {/* {videoPreview} */}
              <VideoPreview
                videoUrl={videoPreview}
                onDelete={handleVideoDelete}
              />
            </div>
          ) : (
            <>
              <Button
                size="small"
                icon={<RxUpload className="text-lg" />}
                className="bg-secondary h-10 border-0 px-6 rounded-xl flex items-center"
                onClick={() => document.getElementById("file-upload").click()} // Trigger file input
              >
                Upload a Video
              </Button>
              <input
                type="file"
                id="file-upload"
                accept="video/*"
                style={{ display: "none" }}
                onChange={handleFileUpload}
              />
              <span className="text-gray-500">or</span>
              <Button
                size="small"
                icon={<HiOutlineVideoCamera className="text-lg" />}
                className="bg-secondary h-9 border-0 px-6 rounded-xl flex items-center"
                onClick={() => setVideoModalVisible(true)}
              >
                Record a Video
              </Button>

              <span className="text-gray-500">or</span>
              <Button
                size="small"
                icon={<FaBox className="text-lg" />}
                className="bg-secondary h-9 border-0 px-6 rounded-xl flex items-center"
                onClick={() => setBucketModalVisible(true)}
              >
                Pick From Bucket
              </Button>
              <p className="w-full text-center text-xs text-gray-500 m-0 basis-full">
                Uploaded files: max {MAX_TEMPLATE_VIDEO_UPLOAD_MB} MB
              </p>
            </>
          )}
        </div>

        {/* Right Side - Question Details */}
        <div className="space-y-4 ">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Form.Item
              layout="vertical"
              label="Question #"
              className="mb-0"
              name="questionNumber"
              rules={[
                { required: true, message: "Question number is required" },
              ]}
            >
              <Input type="number" disabled className="h-11 rounded-xl" />
            </Form.Item>
            <Form.Item
              layout="vertical"
              label="Question Title"
              className="col-span-2 mb-0"
              name="questionTitle"
              rules={[
                { required: true, message: "Question title is required" },
              ]}
            >
              <Input placeholder="Type here..." className="h-11 rounded-xl" />
            </Form.Item>
          </div>
          <Form.Item
            layout="vertical"
            name="description"
            // rules={[{ required: true, message: 'Description is required' }]}
            label="Description"
            className="mb-0"
          >
            <Input.TextArea
              size="large"
              placeholder="Type here..."
              className="rounded-xl"
              rows={4}
            />
          </Form.Item>
        </div>
      </div>
      {questionNumber === 0 && (
        <Form.Item
          label="First Page Settings"
          className="col-span-2 border-t pt-4 mt-4"
        >
          <div className="space-y-2">
            <Form.Item
              name="showTerms"
              valuePropName="checked"
              initialValue={false}
              getValueProps={(value) => ({
                checked: value === undefined ? false : value,
              })}
              noStyle
            >
              <Checkbox>Show Terms & Conditions checkbox</Checkbox>
            </Form.Item>
            <Form.Item
              name="showAnonymous"
              valuePropName="checked"
              initialValue={false}
              getValueProps={(value) => ({
                checked: value === undefined ? false : value,
              })}
              noStyle
            >
              <Checkbox>Show Anonymous Response option</Checkbox>
            </Form.Item>
          </div>
        </Form.Item>
      )}
      <div>
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="flex items-center gap-4">
              <span className="text-gray-700 text-xl font-medium">
                Conditional Trigger
              </span>
              <Switch
                size="large"
                className="custom-switch"
                style={{
                  width: "70px",
                  height: "35px",
                }}
                checked={conditionalEnabled}
                onChange={(checked) => {
                  setConditionalEnabled(checked);
                  if (!checked) {
                    // Clear conditional values when disabled
                    mainForm.setFieldsValue({
                      conditional_question: undefined,
                      conditional_response: undefined,
                    });
                  }
                }}
              />
            </div>

            {conditionalEnabled && (
              <>
                <Form.Item
                  name="conditional_question"
                  label="Question"
                  className="mb-0 col-span-2"
                >
                  <Select
                    size="large"
                    placeholder="Select--"
                    className="w-full"
                    onChange={(value) => {
                      mainForm.setFieldsValue({
                        conditional_response: undefined,
                      });
                      // Force re-render to update options
                      setInputs([...inputs]);
                    }}
                  >
                    {questionsData?.data
                      ?.filter(
                        (q) =>
                          // Don't show current question and only show questions with valid options
                          q._id !== questionId &&
                          q.response?.some(
                            (r) =>
                              (r.input_type === "dropdown" ||
                                r.input_type === "radio" ||
                                r.input_type === "checkbox") &&
                              r.input_options?.length > 0
                          )
                      )
                      .map((question) => (
                        <Option key={question._id} value={question._id}>
                          {`Question ${question.number}: ${question.title}`}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="conditional_response"
                  label="Response"
                  className="mb-0 col-span-2"
                >
                  <Select
                    size="large"
                    placeholder="Select Response"
                    className="w-full"
                    disabled={!mainForm.getFieldValue("conditional_question")}
                  >
                    {(() => {
                      const selectedQuestionId = mainForm.getFieldValue(
                        "conditional_question"
                      );
                      const selectedQuestion = questionsData?.data?.find(
                        (q) => q._id === selectedQuestionId
                      );

                      if (!selectedQuestion) return [];

                      // Get options from dropdown, radio, and checkbox inputs only
                      const allOptions = selectedQuestion.response?.reduce(
                        (acc, r) => {
                          if (
                            ["dropdown", "radio", "checkbox"].includes(
                              r.input_type
                            )
                          ) {
                            const options = Array.isArray(r.input_options)
                              ? r.input_options
                              : typeof r.input_options === "string"
                              ? r.input_options
                                  .split("\n")
                                  .map((opt) => opt.trim())
                                  .filter(Boolean)
                              : [];
                            return [...acc, ...options];
                          }
                          return acc;
                        },
                        []
                      );

                      return allOptions.map((option, index) => (
                        <Option key={`${index}-${option}`} value={option}>
                          {option}
                        </Option>
                      ));
                    })()}
                  </Select>
                </Form.Item>
              </>
            )}
          </div>
        </div>
        <div className="mt-4">
          <QuestionButtonConfig questionNumber={questionNumber} />
        </div>
        <div className="flex justify-between items-center mb-4 mt-4">
          <h3 className="text-md font-semibold m-0">Response Input</h3>
          <div className="relative">
            <Button
              type="default"
              onClick={() => setIsAddingNew(!isAddingNew)}
              className="rounded-full bg-gray-50 border-none h-9"
            >
              + Add Input Field
            </Button>
            {isAddingNew && (
              <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl shadow-lg border border-gray-100 w-48 py-2 z-50 max-h-64 overflow-y-auto">
                {inputTypeOptions.map((option) => (
                  <Button
                    key={option.value}
                    type="text"
                    block
                    className="text-left hover:bg-gray-50 h-9"
                    onClick={() => addInput(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="inputs-list" type="FORM_ELEMENTS">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-4"
              >
                {inputs.map((input, index) => (
                  <Draggable
                    key={input.id}
                    draggableId={input.id}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="bg-white rounded-2xl border border-gray-200 p-6 mb-4"
                      >
                        <div className="flex items-start gap-6 ">
                          <div className="flex flex-col items-center gap-4 ">
                            <CloseOutlined
                              className="text-red-500"
                              onClick={() => removeInput(input.id)}
                            />
                            <p className="text-gray-400">{index + 1}</p>
                            <div
                              {...provided.dragHandleProps}
                              className="text-black "
                            >
                              <MenuOutlined className="rotate-90 " />
                            </div>
                          </div>

                          <div className="flex-1">
                            <div className="flex gap-4 w-full flex-wrap">
                              <Form.Item
                                label="Required"
                                className="mb-0"
                                valuePropName="checked"
                                name={`required_${input.id}`}
                              >
                                <Checkbox
                                  checked={input.require}
                                  onChange={(e) => {
                                    updateInput(
                                      input.id,
                                      "require",
                                      e.target.checked
                                    );
                                    mainForm.setFieldValue(
                                      `required_${input.id}`,
                                      e.target.checked
                                    );
                                  }}
                                >
                                  Required field
                                </Checkbox>
                              </Form.Item>
                              <Form.Item
                                layout="vertical"
                                label="Input Type"
                                className="mb-0 md:w-[30%] w-full"
                              >
                                <Select
                                  size="large"
                                  value={input.type}
                                  onChange={(value) =>
                                    updateInput(input.id, "type", value)
                                  }
                                  className="w-full"
                                  dropdownRender={(menu) => (
                                    <div onMouseDown={preventDragHandling}>
                                      {menu}
                                    </div>
                                  )}
                                >
                                  {inputTypeOptions.map((option) => (
                                    <Option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </Option>
                                  ))}
                                </Select>
                              </Form.Item>

                              <Form.Item
                                layout="vertical"
                                label={`${getInputTypeLabel(input.type)} Title`}
                                className="mb-0 flex-1"
                                name={`input_title_${input.id}`}
                                // rules={[{
                                //   require: input.type !== 'slider',
                                //   message: 'Input Title is require'
                                // }]}
                              >
                                <Input
                                  size="large"
                                  value={input.title}
                                  onChange={(e) => {
                                    updateInput(
                                      input.id,
                                      "title",
                                      e.target.value
                                    );
                                    mainForm.setFieldValue(
                                      `input_title_${input.id}`,
                                      e.target.value
                                    );
                                  }}
                                  placeholder={
                                    input.type === "slider"
                                      ? "Title (Optional)"
                                      : "Type here..."
                                  }
                                />
                              </Form.Item>

                              {input.type === "slider" && (
                                <div className="space-y-4 w-full">
                                  {/* Scale Type */}
                                  <Form.Item
                                    layout="vertical"
                                    label="Scale Type"
                                    className="mb-0"
                                    name={`scale_type_${input.id}`}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please select a Scale Type",
                                      },
                                    ]}
                                  >
                                    <Select
                                      size="large"
                                      allowClear
                                      placeholder="Select scale type"
                                      value={
                                        mainForm.getFieldValue(
                                          `scale_type_${input.id}`,
                                        ) ?? undefined
                                      }
                                      onChange={(value) => {
                                        const current = inputs.find(
                                          (item) => item.id === input.id,
                                        );
                                        const nextLabels =
                                          parseSliderOptionLabels(
                                            current?.options,
                                            value ?? null,
                                          );
                                        const joined =
                                          joinSliderOptionLabels(nextLabels);

                                        updateInput(input.id, "options", joined);
                                        updateInput(
                                          input.id,
                                          "scale_type",
                                          value ?? null,
                                        );
                                        mainForm.setFieldValue(
                                          `slider_options_${input.id}`,
                                          joined,
                                        );
                                        mainForm.setFieldValue(
                                          `scale_type_${input.id}`,
                                          value ?? null,
                                        );
                                        nextLabels.forEach((label, index) => {
                                          mainForm.setFieldValue(
                                            `slider_option_${input.id}_${index}`,
                                            label,
                                          );
                                        });
                                      }}
                                      className="w-full rounded-xl"
                                      dropdownRender={(menu) => (
                                        <div onMouseDown={preventDragHandling}>
                                          {menu}
                                        </div>
                                      )}
                                    >
                                      {getSliderScaleSelectOptions(
                                        mainForm.getFieldValue(
                                          `scale_type_${input.id}`,
                                        ) ?? input.scale_type,
                                      ).map((option) => (
                                        <Option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>

                                  <Form.Item
                                    layout="vertical"
                                    label="Show Emojis"
                                    className="mb-0"
                                    valuePropName="checked"
                                    name={`show_emojis_${input.id}`}
                                  >
                                    <Switch
                                      className="custom-switch"
                                      style={{ width: "70px", height: "35px" }}
                                      checked={Boolean(input.show_emojis)}
                                      onChange={(checked) => {
                                        const next = Boolean(checked);
                                        mainForm.setFieldValue(
                                          `show_emojis_${input.id}`,
                                          next,
                                        );
                                        if (next) {
                                          mainForm.setFieldValue(
                                            `show_stars_${input.id}`,
                                            false,
                                          );
                                        }
                                        updateInput(input.id, {
                                          show_emojis: next,
                                          ...(next ? { show_stars: false } : {}),
                                        });
                                      }}
                                    />
                                  </Form.Item>

                                  <Form.Item
                                    layout="vertical"
                                    label="Show Stars"
                                    className="mb-0"
                                    valuePropName="checked"
                                    name={`show_stars_${input.id}`}
                                  >
                                    <Switch
                                      className="custom-switch"
                                      style={{ width: "70px", height: "35px" }}
                                      checked={Boolean(input.show_stars)}
                                      onChange={(checked) => {
                                        const next = Boolean(checked);
                                        mainForm.setFieldValue(
                                          `show_stars_${input.id}`,
                                          next,
                                        );
                                        if (next) {
                                          mainForm.setFieldValue(
                                            `show_emojis_${input.id}`,
                                            false,
                                          );
                                        }
                                        updateInput(input.id, {
                                          show_stars: next,
                                          ...(next ? { show_emojis: false } : {}),
                                        });
                                      }}
                                    />
                                  </Form.Item>

                                  <Form.Item
                                    layout="vertical"
                                    label="No Text"
                                    className="mb-0"
                                    valuePropName="checked"
                                    name={`show_only_emojis_${input.id}`}
                                  >
                                    <Switch
                                      className="custom-switch"
                                      style={{ width: "70px", height: "35px" }}
                                      checked={Boolean(input.show_only_emojis)}
                                      onChange={(checked) => {
                                        const next = Boolean(checked);
                                        mainForm.setFieldValue(
                                          `show_only_emojis_${input.id}`,
                                          next,
                                        );
                                        updateInput(input.id, {
                                          show_only_emojis: next,
                                          ...(next ? { options: "" } : {}),
                                        });
                                        if (next) {
                                          mainForm.setFieldValue(
                                            `slider_options_${input.id}`,
                                            "",
                                          );
                                        }
                                      }}
                                    />
                                  </Form.Item>

                                  {!input.show_only_emojis &&
                                    (mainForm.getFieldValue(
                                      `scale_type_${input.id}`,
                                    ) ||
                                      input.scale_type) && (
                                    <div className="space-y-3 w-full">
                                      <div className="text-sm font-medium text-gray-700">
                                        Scale point labels (left to right)
                                      </div>
                                      <Form.Item
                                        name={`slider_options_${input.id}`}
                                        hidden
                                      >
                                        <Input type="hidden" />
                                      </Form.Item>
                                      {parseSliderOptionLabels(
                                        input.options,
                                        mainForm.getFieldValue(
                                          `scale_type_${input.id}`,
                                        ),
                                      ).map((label, pointIndex, labels) => (
                                        <Form.Item
                                          key={`slider_option_${input.id}_${pointIndex}`}
                                          layout="vertical"
                                          className="mb-0"
                                          label={`Point ${pointIndex + 1}${
                                            pointIndex === 0
                                              ? " (left)"
                                              : pointIndex === labels.length - 1
                                                ? " (right)"
                                                : ""
                                          }`}
                                          name={`slider_option_${input.id}_${pointIndex}`}
                                          rules={[
                                            {
                                              validator: async (_, value) => {
                                                if (
                                                  mainForm.getFieldValue(
                                                    `show_only_emojis_${input.id}`,
                                                  )
                                                ) {
                                                  return Promise.resolve();
                                                }

                                                if (
                                                  !mainForm.getFieldValue(
                                                    `scale_type_${input.id}`,
                                                  )
                                                ) {
                                                  return Promise.resolve();
                                                }

                                                if (
                                                  isMissingSliderOptionLabel(
                                                    value,
                                                  )
                                                ) {
                                                  throw new Error(
                                                    "Enter a label for this scale point",
                                                  );
                                                }

                                                return Promise.resolve();
                                              },
                                            },
                                          ]}
                                        >
                                          <Input
                                            size="large"
                                            className="rounded-xl"
                                            placeholder={`Label for point ${pointIndex + 1}`}
                                            value={label}
                                            onChange={(e) => {
                                              const scaleType =
                                                mainForm.getFieldValue(
                                                  `scale_type_${input.id}`,
                                                );
                                              const nextLabels =
                                                parseSliderOptionLabels(
                                                  input.options,
                                                  scaleType,
                                                );
                                              nextLabels[pointIndex] =
                                                e.target.value;
                                              const joined =
                                                joinSliderOptionLabels(
                                                  nextLabels,
                                                );
                                              updateInput(
                                                input.id,
                                                "options",
                                                joined,
                                              );
                                              mainForm.setFieldValue(
                                                `slider_options_${input.id}`,
                                                joined,
                                              );
                                              mainForm.setFieldValue(
                                                `slider_option_${input.id}_${pointIndex}`,
                                                e.target.value,
                                              );
                                            }}
                                          />
                                        </Form.Item>
                                      ))}
                                    </div>
                                  )}

                                  <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                                    <div className="text-sm font-medium mb-2">
                                      Preview:
                                    </div>
                                    <div className="flex justify-between items-center">
                                      {(() => {
                                        const scaleType =
                                          mainForm.getFieldValue(
                                            `scale_type_${input.id}`,
                                          );
                                        const showOnlyEmojis =
                                          toSliderToggleValue(
                                            mainForm.getFieldValue(
                                              `show_only_emojis_${input.id}`,
                                            ) ?? input.show_only_emojis,
                                          );
                                        const showEmojis = toSliderToggleValue(
                                          mainForm.getFieldValue(
                                            `show_emojis_${input.id}`,
                                          ) ?? input.show_emojis,
                                        );
                                        const showStars = toSliderToggleValue(
                                          mainForm.getFieldValue(
                                            `show_stars_${input.id}`,
                                          ) ?? input.show_stars,
                                        );
                                        const numberOfPoints = parseInt(
                                          scaleType,
                                          10,
                                        );

                                        if (
                                          !Number.isInteger(numberOfPoints) ||
                                          numberOfPoints < 1
                                        ) {
                                          return (
                                            <span className="text-sm text-gray-500">
                                              Select a Scale Type to preview
                                            </span>
                                          );
                                        }

                                        const displayItems = showOnlyEmojis
                                          ? Array(numberOfPoints).fill("")
                                          : parseSliderOptionLabels(
                                              input.options,
                                              numberOfPoints,
                                            );

                                        return displayItems.map(
                                          (option, idx) => (
                                            <div
                                              key={idx}
                                              className="flex flex-col items-center"
                                            >
                                              {(showEmojis ||
                                                showOnlyEmojis ||
                                                showStars) && (
                                                <span className="text-xl mb-1">
                                                  {showStars
                                                    ? getReactStar(
                                                        idx,
                                                        numberOfPoints,
                                                      )
                                                    : getReactEmoji(
                                                        idx,
                                                        numberOfPoints,
                                                      )}
                                                </span>
                                              )}
                                              {!showOnlyEmojis && (
                                                <span className="text-sm">
                                                  {String(option ?? "").trim() ||
                                                    `Point ${idx + 1}`}
                                                </span>
                                              )}
                                            </div>
                                          ),
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {(input.type === "checkbox" ||
                                input.type === "radio" ||
                                input.type === "dropdown") && (
                                <Form.Item layout="vertical" className="mb-0 flex-1">
                                  <ChoiceOptionsEditor
                                    input={input}
                                    updateInput={updateInput}
                                    mainForm={mainForm}
                                    uploadFile={uploadFile}
                                    preventDragHandling={preventDragHandling}
                                    optionLabel={
                                      input.type === "checkbox"
                                        ? "Checkbox Options"
                                        : input.type === "radio"
                                          ? "Radio Options"
                                          : "Drop Down Options"
                                    }
                                  />
                                </Form.Item>
                              )}

                              {isRadioListInputType(input.type) && (
                                <RadioListEditor
                                  input={input}
                                  updateInput={updateInput}
                                />
                              )}

                              {input.type === "date" && (
                                <Form.Item
                                  layout="vertical"
                                  label="Date Type"
                                  className="mb-0 flex-1"
                                >
                                  <Select
                                    size="large"
                                    value={input.dateType}
                                    onChange={(value) =>
                                      updateInput(input.id, "dateType", value)
                                    }
                                    className="w-full"
                                    dropdownRender={(menu) => (
                                      <div onMouseDown={preventDragHandling}>
                                        {menu}
                                      </div>
                                    )}
                                  >
                                    {dateTypes.map((option) => (
                                      <Option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              )}

                              {(input.type === "file" ||
                                input.type === "multiple-files") && (
                                <Form.Item
                                  layout="vertical"
                                  label="File Type"
                                  className="mb-0 flex-1"
                                >
                                  <Select
                                    size="large"
                                    value={input.fileType}
                                    onChange={(value) =>
                                      updateInput(input.id, "fileType", value)
                                    }
                                    className="w-full"
                                    dropdownRender={(menu) => (
                                      <div onMouseDown={preventDragHandling}>
                                        {menu}
                                      </div>
                                    )}
                                  >
                                    {fileTypes.map((option) => (
                                      <Option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              )}

                              {input.type === "record-video" && (
                                <Form.Item
                                  layout="vertical"
                                  label="Recording Limit Duration"
                                  className="mb-0 flex-1"
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        "Please select a recording duration",
                                    },
                                  ]}
                                >
                                  <input
                                    type="time"
                                    step="1"
                                    className="ant-input ant-input-lg w-full h-11 rounded-2xl border border-gray-700 px-2"
                                    value={input.recordingLimit || "00:00:00"}
                                    onChange={(e) => {
                                      const timeValue = e.target.value;
                                      updateInput(
                                        input.id,
                                        "recordingLimit",
                                        timeValue
                                      );
                                    }}
                                  />
                                  <div className="text-xs text-gray-500 mt-1">
                                    Format: HH:MM:SS (e.g., 00:02:30 for 2
                                    minutes 30 seconds)
                                  </div>
                                </Form.Item>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {renderVideoModal()}
      {renderUploadProgress()}
      <VideoBucketModal
        open={bucketModalVisible}
        onCancel={() => setBucketModalVisible(false)}
        videos={videosData?.data?.videoCatalog || []}
        onSelect={handleBucketVideoSelect}
        selectedVideoUrl={uploadedVideo}
        selectedVideo={selectedVideo}
        setSelectedVideo={setSelectedVideo}
        organizations={orgsData?.data || []}
        interpreters={interpretersData?.data || []}
        onFiltersChange={setVideoFilters}
      />
      <UploadConfirmationModal
        visible={showUploadConfirmation}
        onClose={handleCloseUploadConfirmation}
        onUploadToCatalog={handleUploadToCatalog}
        videoPreview={pendingCatalogVideo}
      />
      <Modal
        title="Upload Video"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          uploadModalForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={uploadModalForm}
          layout="vertical"
          onFinish={handleUpload}
          className="mt-4"
        >
          <Form.Item
            name="title"
            label="Video Title"
            rules={[{ required: true, message: "Please enter video title" }]}
          >
            <Input placeholder="Type here..." className="rounded-xl" />
          </Form.Item>

          <Form.Item
            name="language"
            label="Sign Language"
            className="w-full"
            rules={[
              { required: true, message: "Please select a sign language" },
            ]}
          >
            <Select placeholder="Select language" className="rounded-xl h-11">
              {languageOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Video Description"
            rules={[
              { required: true, message: "Please enter video description" },
            ]}
          >
            <Input.TextArea
              placeholder="Type here..."
              rows={4}
              className="rounded-xl"
            />
          </Form.Item>

          <div className="flex gap-2">
            <Form.Item
              name="interpreter"
              className="w-full"
              label="Presenter / Interpreter"
              rules={[{ required: true, message: "Please select a presenter" }]}
            >
              <Select placeholder="Select--" className="rounded-xl h-11">
                {interpretersData?.data
                  ?.filter(
                    (interpreter) =>
                      interpreter.status?.toLowerCase() !== "inactive"
                  )
                  ?.map((interpreter) => (
                    <Option key={interpreter._id} value={interpreter._id}>
                      {interpreter?.user?.full_name || "Unnamed Interpreter"}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="organization"
              className="w-full"
              label="Associated Organisation (Optional)"
            >
              <Select placeholder="Select--" className="rounded-xl h-11">
                {orgsData?.data?.map((org) => (
                  <Option key={org._id} value={org._id}>
                    {org.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          {/* Video Preview Section */}
          <div className="mb-4">
            <div className="font-medium mb-2">Video Preview</div>
            <video
              src={pendingCatalogVideo}
              className="w-full rounded-xl"
              style={{ maxHeight: "200px" }}
              autoPlay
              muted
              loop
              playsInline
            />
          </div>

          <Form.Item className="mb-0">
            <Button
              type="primary"
              htmlType="submit"
              loading={uploading}
              className="w-full h-12 rounded-xl bg-[#18181B]"
            >
              Upload to Catalogue
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Form>
  );
};

export default CreateQuestionTemplate;
